// Filtres : recherche, type, statut, non lus, les miens / des autres, et la
// vue de pilotage (archives, corbeille, tri, auteur) pour les admins.
// Porté de FeedbackFilters.
import type { JSX } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import type { ArchivedView, Counts, FeedbackType, Sort } from '@feedbaacks/sdk'
import { DEFAULT_FILTERS, useFeedbaacks, type StatusGroup } from '../context'
import { Search, Shield, X } from '../icons'
import { ChipRow, ToggleChip } from '../primitives'
import { useSelector } from '../store'
import { isDefaultView } from './List'

export function Filters(): JSX.Element | null {
  const { client, store, t, role, realtime } = useFeedbaacks()
  const open = useSelector(store, (s) => s.filtersOpen)
  const filters = useSelector(store, (s) => s.filters)
  const authors = useSelector(store, (s) => s.knownAuthors)
  const [counts, setCounts] = useState<Counts | null>(null)
  const [search, setSearch] = useState(filters.search)
  const set = (patch: Partial<typeof filters>) => store.set((s) => ({ filters: { ...s.filters, ...patch } }))

  useEffect(() => {
    if (!open) return
    const load = () => client.feedback.counts().then(setCounts, () => {})
    void load()
    return realtime.on(() => void load())
  }, [open, client, realtime])
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== store.get().filters.search) set({ search })
    }, 300)
    return () => clearTimeout(timer)
  }, [search])
  useEffect(() => setSearch(filters.search), [filters.search])

  if (!open) return null
  const types: Array<{ value: FeedbackType | 'all'; label: string; count?: number }> = [
    { value: 'all', label: t('filter_all'), count: counts?.total },
    { value: 'feature', label: t('type_features'), count: counts?.by_type.feature },
    { value: 'bug', label: t('type_bugs'), count: counts?.by_type.bug },
    { value: 'question', label: t('type_questions'), count: counts?.by_type.question },
  ]
  const g = counts?.by_status_group
  const statuses: Array<{ value: StatusGroup; label: string; count?: number }> = [
    { value: 'active', label: t('status_active'), count: g ? g.open + g.in_progress : undefined },
    { value: 'open', label: t('status_open'), count: g?.open },
    { value: 'in_progress', label: t('status_in_progress'), count: g?.in_progress },
    { value: 'on_hold', label: t('status_on_hold'), count: g?.on_hold },
    { value: 'shipped', label: t('status_shipped'), count: g?.shipped },
    { value: 'declined', label: t('status_declined'), count: g?.declined },
    { value: 'all', label: t('filter_all'), count: counts?.total },
  ]
  return (
    <div class="fb-filters" role="region" aria-label={t('filters_region_aria')} data-testid="feedbaacks-filters">
      <div class="fb-search">
        <Search size={14} />
        <input type="search" class="fb-input" value={search} placeholder={t('filter_search_placeholder')} aria-label={t('filter_search_aria')}
          data-testid="feedbaacks-search" onInput={(e) => setSearch(e.currentTarget.value)} />
        {search && (
          <button type="button" class="fb-icon-btn" aria-label={t('filter_search_clear')} onClick={() => setSearch('')}>
            <X size={12} />
          </button>
        )}
      </div>
      <ChipRow label={t('filter_type_aria')} options={types} value={filters.type} onChange={(type) => set({ type })} />
      <ChipRow label={t('filter_status_aria')} options={statuses} value={filters.statusGroup} onChange={(statusGroup) => set({ statusGroup })} />
      <div class="fb-chips">
        <ToggleChip label={t('filter_unread')} checked={filters.unread} onChange={(unread) => set({ unread })} testId="feedbaacks-filter-unread" />
        <ToggleChip label={t('filter_mine')} checked={filters.mine} count={counts?.mine} onChange={(mine) => set({ mine, others: false })} testId="feedbaacks-filter-mine" />
        <ToggleChip label={t('filter_others')} checked={filters.others} count={counts ? Math.max(0, counts.total - counts.mine) : undefined}
          onChange={(others) => set({ others, mine: false })} testId="feedbaacks-filter-others" />
        {!isDefaultView(filters) && (
          <button type="button" class="fb-link" onClick={() => store.set({ filters: DEFAULT_FILTERS })} data-testid="feedbaacks-filters-reset">{t('filters_reset')}</button>
        )}
      </div>
      {role === 'admin' && (
        <div class="fb-admin-filters" data-testid="feedbaacks-admin-filters">
          <div class="fb-section-title">
            <Shield size={12} />
            {t('admin_filters')}
          </div>
          <ChipRow<ArchivedView> label={t('archive_view_aria')} value={filters.archived} onChange={(archived) => set({ archived })}
            options={[{ value: 'active', label: t('archive_view_active') }, { value: 'archived', label: t('archive_view_archived') }, { value: 'all', label: t('archive_view_all') }]} />
          <ChipRow<Sort> label={t('sort_aria')} value={filters.sort} onChange={(sort) => set({ sort })}
            options={[{ value: 'score', label: t('sort_score') }, { value: 'recent', label: t('sort_recent') }, { value: 'comments', label: t('sort_comments') }, { value: 'updated', label: t('sort_updated') }]} />
          <div class="fb-chips">
            <ToggleChip label={t('filter_trash')} checked={filters.trash} onChange={(trash) => set({ trash })} testId="feedbaacks-filter-trash" />
            <select class="fb-select" aria-label={t('filter_author')} value={filters.authorId ?? ''} onChange={(e) => set({ authorId: e.currentTarget.value || null })}>
              <option value="">{t('filter_all_authors')}</option>
              {[...authors].sort((a, b) => a.name.localeCompare(b.name)).map((a) => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  )
}
